import { FC } from "react";

import { DireProgressBar } from "@/components/Layout/ProgressBar/DireProgressBar";

import { StepMenu } from "./StepMenu";

export const formatStep = (index: number) => String(index + 1).padStart(2, "0");

interface Props {
  /** Title of every step, in order — also the labels of the jump menu. */
  titles: string[];
  currentStep: number;
  /** Fill of the current segment, 0 to 1, for a timed step. */
  progress?: number;
  counterLabel: string;
  menuLabel: string;
  /** Accessible name of a segment, e.g. "Go to slide 03". */
  stepLabel: (step: string) => string;
  onStepChange: (index: number) => void;
}

/**
 * Story-style stepper on a dark surface: one progress segment per step, the
 * current step's title, the step counter and a menu to jump to a step.
 */
export const SlideStepper: FC<Props> = ({
  titles,
  currentStep,
  progress = 0,
  counterLabel,
  menuLabel,
  stepLabel,
  onStepChange,
}) => (
  <div className="flex flex-col">
    <div className="flex gap-1">
      {titles.map((title, index) => (
        <button
          key={title}
          className="flex-1"
          type="button"
          aria-label={stepLabel(formatStep(index))}
          onClick={() => onStepChange(index)}
        >
          <DireProgressBar
            className="flex h-1 w-full items-start gap-0.5"
            value={
              index < currentStep
                ? 100
                : index === currentStep
                  ? progress * 100
                  : 0
            }
            inverted
          />
        </button>
      ))}
    </div>

    <div className="flex items-center justify-between gap-2">
      <p className="flex-1 truncate text-body-sm font-semibold leading-none text-foreground-primary-inverse">
        {titles[currentStep]}
      </p>
      <p className="text-body-sm leading-none text-foreground-primary-inverse">
        {counterLabel}
      </p>
      <StepMenu
        items={titles.map((title) => ({ label: title }))}
        currentIndex={currentStep}
        onSelect={onStepChange}
        toggleLabel={menuLabel}
      />
    </div>
  </div>
);
